import { instance } from "api/apiBase";
import { Applicant } from "types/application";
import { OpenReverificationRequest } from "shared/types";
import {
  ApplicationDecision,
  ApplicationMetricsResponse,
  ApplicationMetricTemplateEntry,
  ApplicationNeighbors,
  ApplicationProgress,
  ApplicationSummary
} from "./types";

const api = {
  getApplicationsProgress: async (
    applicationIds: number[]
  ): Promise<ApplicationProgress[]> => {
    return (
      await instance.get<{ applications: ApplicationProgress[] }>(
        "/portal/v1/applications/progress",
        { params: { application_ids: applicationIds } }
      )
    ).data.applications;
  },
  // `listQuery` is the applications list query string minus paging; without
  // it the API uses the default Applications list.
  getApplicationNeighbors: async (
    id: string,
    listQuery?: string
  ): Promise<ApplicationNeighbors> => {
    const url = `/portal/v1/applications/${id}/neighbors`;
    return (
      await instance.get<ApplicationNeighbors>(
        listQuery ? `${url}?${listQuery}` : url
      )
    ).data;
  },
  getApplicationSummary: async (id: string): Promise<ApplicationSummary> => {
    return (
      await instance.get<ApplicationSummary>(
        `/portal/v1/applications/${id}/summary`
      )
    ).data;
  },
  getApplicationDecision: async (id: string): Promise<ApplicationDecision> => {
    return (
      await instance.get<ApplicationDecision>(
        `/portal/v1/applications/${id}/decision`
      )
    ).data;
  },
  getApplicationMetrics: async (
    id: string
  ): Promise<ApplicationMetricsResponse> => {
    return (
      await instance.get<ApplicationMetricsResponse>(
        `/portal/v1/applications/${id}/metrics`
      )
    ).data;
  },
  updateApplicationMetricsTemplate: async (
    template: ApplicationMetricTemplateEntry[]
  ): Promise<{ template: ApplicationMetricTemplateEntry[] }> => {
    return (
      await instance.patch(`/portal/v1/users/application_metrics_template`, {
        template
      })
    ).data;
  },
  resetApplicationMetricsTemplate: async (): Promise<void> => {
    await instance.delete(`/portal/v1/users/application_metrics_template`);
  },
  getApplicantDetail: async (
    applicationId: string,
    applicantId: string
  ): Promise<Applicant> => {
    return (
      await instance.get<Applicant>(
        `/portal/v1/applications/${applicationId}/applicants/${applicantId}`
      )
    ).data;
  },
  createDocumentRequest: async ({
    applicationId,
    applicantId,
    note
  }: {
    applicationId: string;
    applicantId: string;
    note: string;
  }): Promise<Applicant> => {
    return (
      await instance.post<Applicant>(
        `/portal/v1/applications/${applicationId}/applicants/${applicantId}/document_requests`,
        { note }
      )
    ).data;
  },
  cancelDocumentRequest: async ({
    applicationId,
    applicantId,
    documentRequestId
  }: {
    applicationId: string;
    applicantId: string;
    documentRequestId: number;
  }): Promise<Applicant> => {
    return (
      await instance.patch<Applicant>(
        `/portal/v1/applications/${applicationId}/applicants/${applicantId}/document_requests/${documentRequestId}/cancel`
      )
    ).data;
  },
  markDocumentRequestsSeen: async ({
    applicantId,
    applicationId
  }: {
    applicationId: string;
    applicantId: string;
  }): Promise<Applicant> => {
    return (
      await instance.patch<Applicant>(
        `/portal/v1/applications/${applicationId}/applicants/${applicantId}/document_requests/mark_seen`
      )
    ).data;
  },
  cancelOpenReverificationRequest: async ({
    applicationId,
    request
  }: {
    applicationId: string;
    request: OpenReverificationRequest;
  }): Promise<void> => {
    const applicantPath = `/portal/v1/applications/${applicationId}/applicants/${request.applicant_id}`;

    switch (request.cancel_action) {
      case "income_reverify_request_cancel":
        await instance.post(
          `/portal/v1/applications/${applicationId}/verifications/${request.verification_id}/reverify_requests/cancel?applicant_id=${request.applicant_id}`,
          { request_id: request.request_id }
        );
        return;
      case "combined_income_rerun_cancel":
        await instance.post(
          `${applicantPath}/verifications/${request.verification_id}/cancel_rerun`
        );
        return;
      case "identity_retry_cancel":
        await instance.post(
          `${applicantPath}/cancel_request_retry_identity_verification`
        );
        return;
      case "identity_rerun_cancel":
        await instance.post(
          `${applicantPath}/cancel_rerun_identity_verification`
        );
        return;
    }
  }
};

export default api;
